import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EntityType } from '@mariana/labos-client';
import type { EventLogEntry } from '@mariana/labos-client';
import { limsClient } from '../api/limsClient';
import { EntityFilters } from './EntityFilters';
import { EntityTable } from './EntityTable';
import { EntityDetail } from './EntityDetail';
import { ViewSearchBar } from './ViewSearchBar';
import { CopyLinkButton } from './CopyLinkButton';
import { formatDate } from '../utils/formatDate';
import { capitalize } from '../utils/formatValue';
import { getEntityColumns } from '../config/viewsConfig';
import { Breadcrumb } from './Breadcrumb';
import { useViewState } from '../hooks/useViewState';
import { useEditMode } from '../context/EditModeContext';
import { useLab, entityHasLabId } from '../LabContext';
import './EntityView.css';

interface EntityViewProps {
  entityType: EntityType;
}

export const EntityView: React.FC<EntityViewProps> = ({ entityType }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const [recentEvents, setRecentEvents] = useState<EventLogEntry[]>([]);
  const [activityOpen, setActivityOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const { isEditMode } = useEditMode();

  const { labSelection, labFilters, eventLabId } = useLab();
  const hasLabId = entityHasLabId(entityType);

  const allColumns = getEntityColumns(entityType, labSelection);
  const parsedId = id ? parseInt(id, 10) : null;
  const selectedEntityId = parsedId !== null && !isNaN(parsedId) ? parsedId : null;

  const {
    entities,
    resolvedEntities,
    pagination,
    filters,
    sortBy,
    sortOrder,
    loading,
    error,
    handleFiltersChange,
    handlePageChange,
    handleLimitChange,
    handleSortChange,
    refreshEntities,
    visibleColumns,
    setVisibleColumns,
    searchQuery,
    setSearchQuery,
    fetchAllEntities,
  } = useViewState({
    entityType,
    allColumns,
    defaultSort: { by: 'id', order: 'desc' },
    disabled: selectedEntityId !== null,
    extraFilters: hasLabId ? labFilters : undefined,
  });

  useEffect(() => {
    if (id && selectedEntityId === null) {
      navigate(`/${entityType}`, { replace: true });
    }
  }, [id, selectedEntityId, entityType, navigate]);

  useEffect(() => {
    const loadRecentEvents = async () => {
      try {
        const response = await limsClient.getEvents({
          entityType,
          limit: 5,
          labId: hasLabId ? eventLabId : undefined,
        });
        setRecentEvents(response.data);
      } catch (err) {
        console.warn('Failed to load recent events:', err);
      }
    };
    loadRecentEvents();
  }, [entityType, hasLabId, eventLabId]);

  const handleEntityClick = (entityId: number) => {
    const currentSearch = searchParams.toString();
    const url = `/${entityType}/${entityId}${currentSearch ? `?${currentSearch}` : ''}`;
    navigate(url);
  };

  const handleDeleteEntity = async (entityId: number) => {
    setActionError(null);
    try {
      await limsClient.deleteEntityByType(entityType, entityId);
      refreshEntities();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete entity');
    }
  };

  const handleCreateEntity = async () => {
    setActionError(null);
    try {
      const result = await limsClient.createEntityByType(entityType, {});
      if (result.data?.[0]?.id) {
        navigate(`/${entityType}/${result.data[0].id}`);
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to create entity');
    }
  };

  const entityTypeDisplay = capitalize(entityType) + 's';

  if (selectedEntityId !== null) {
    return (
      <div className="entity-detail-wrapper">
        <Breadcrumb items={[
          { label: 'Mariana Labs', path: '/dashboard' },
          { label: entityTypeDisplay, path: `/${entityType}` },
          { label: `#${selectedEntityId}` },
        ]} />
        <EntityDetail
          entityType={entityType}
          entityId={selectedEntityId}
        />
      </div>
    );
  }

  return (
    <div className="entity-view">
      <div className="entity-view-header">
        <Breadcrumb items={[{ label: 'Mariana Labs', path: '/dashboard' }, { label: entityTypeDisplay }]} />
        <div className="entity-view-header-actions">
          {entityType === 'assay' ? (
            // Assays are created through the self-serve form, not as a blank row.
            <button className="create-entity-btn" onClick={() => navigate('/assay/new')}>
              + New Assay
            </button>
          ) : isEditMode && (
            <button className="create-entity-btn" onClick={handleCreateEntity}>
              + New {capitalize(entityType)}
            </button>
          )}
        </div>
      </div>

      {(error || actionError) && (
        <div className="error-message">Error: {error || actionError}</div>
      )}

      <div className="entity-filters-row">
        <EntityFilters
          entityType={entityType}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          entities={entities}
          extraFilters={hasLabId ? labFilters : undefined}
        />
        <CopyLinkButton />
      </div>

      <ViewSearchBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        loading={loading && searchQuery.trim().length > 0}
      />

      <EntityTable
        entities={entities}
        entityType={entityType}
        columns={visibleColumns}
        defaultColumns={allColumns}
        onColumnsChange={setVisibleColumns}
        resolvedEntities={resolvedEntities}
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onEntityClick={handleEntityClick}
        onSortChange={handleSortChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        loading={loading}
        exportName={`${entityType}s`}
        sampleEntities={entities}
        extraFilters={hasLabId ? labFilters : undefined}
        onDeleteEntity={handleDeleteEntity}
        onExportAll={fetchAllEntities}
      />

      {recentEvents.length > 0 && (
        <div className="recent-activity-strip">
          <button
            className="recent-activity-toggle"
            onClick={() => setActivityOpen(!activityOpen)}
          >
            Recent Activity ({recentEvents.length})
            <span className={`toggle-arrow ${activityOpen ? 'open' : ''}`}>▶</span>
          </button>
          {activityOpen && (
            <div className="recent-activity-list">
              {recentEvents.map(event => (
                <div
                  key={event.id}
                  className="recent-activity-item"
                  onClick={() => navigate(`/${event.entityType}/${event.entityId}`)}
                >
                  <span className={`activity-action ${event.action}`}>{event.action}</span>
                  <span className="activity-entity">
                    {event.entityType} #{event.entityId}
                  </span>
                  {event.changedAttributes && event.changedAttributes.length > 0 && (
                    <span className="activity-attrs">
                      {event.changedAttributes.join(', ')}
                    </span>
                  )}
                  <span className="activity-time">{formatDate(event.createdAt)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
